import { useEffect, useState } from "react";
import { Plus, Save, Trash2, Bot } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SectionTitle } from "@/components/shared";
import { adminApi } from "@/lib/apiClient";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const BLANK = { id: "", name: "", title: "", description: "", system_prompt: "", accent: "teal" };

export default function PersonasTab() {
  const [personas, setPersonas] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(BLANK);

  const load = async () => {
    const r = await adminApi.get("/admin/personas");
    setPersonas(r.data.personas);
    if (!selected && r.data.personas.length) {
      setSelected(r.data.personas[0].id);
      setForm(r.data.personas[0]);
    }
  };
  useEffect(() => { load(); }, []);

  const pick = (p) => { setSelected(p.id); setForm(p); };
  const newPersona = () => { setSelected(null); setForm(BLANK); };

  const save = async () => {
    if (!form.name.trim() || !form.system_prompt.trim()) {
      toast.error("Name and system prompt are required");
      return;
    }
    try {
      const r = await adminApi.post("/admin/personas", form);
      toast.success("Persona saved");
      setSelected(r.data.id);
      load();
    } catch (e) { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    try {
      await adminApi.delete(`/admin/personas/${id}`);
      toast.success("Persona deleted");
      newPersona();
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="grid lg:grid-cols-[300px_minmax(0,1fr)] gap-6">
      <div>
        <div className="flex items-center justify-between mb-3">
          <SectionTitle>Personas</SectionTitle>
          <Button size="sm" variant="secondary" onClick={newPersona} data-testid="persona-new-button"><Plus className="h-4 w-4" /> New</Button>
        </div>
        <div className="space-y-2" data-testid="persona-list">
          {personas.map((p) => (
            <button
              key={p.id}
              onClick={() => pick(p)}
              data-testid={`persona-list-item-${p.id}`}
              className={cn(
                "w-full text-left rounded-xl border p-3 transition-colors",
                selected === p.id ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-secondary/50"
              )}
            >
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-secondary grid place-items-center"><Bot className="h-4 w-4 text-primary" /></div>
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{p.name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{p.title}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <Card className="p-6 bg-card border-border">
        <SectionTitle>{selected ? "Edit Persona" : "New Persona"}</SectionTitle>
        <div className="mt-4 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="persona-name-input" className="mt-1 bg-secondary/40" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="persona-title-input" className="mt-1 bg-secondary/40" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Short description</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="persona-description-input" className="mt-1 bg-secondary/40" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">System prompt (persona behavior)</Label>
            <Textarea value={form.system_prompt} onChange={(e) => setForm({ ...form, system_prompt: e.target.value })} data-testid="persona-system-prompt-input" className="mt-1 bg-secondary/40 min-h-[160px]" />
            <p className="mt-1.5 text-[11px] text-muted-foreground">Grounding, citation and zero-exposure rules are always appended by the engine.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={save} data-testid="persona-editor-save-button"><Save className="h-4 w-4" /> Save Persona</Button>
            {selected && (
              <Button variant="ghost" onClick={() => remove(selected)} data-testid="persona-delete-button" className="text-rose-300"><Trash2 className="h-4 w-4" /> Delete</Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
